/**
 * Analytics helper — single source of truth for every dataLayer event.
 *
 * Pattern: pushes a typed event to GTM's dataLayer. GTM tags listen for these
 * event names and forward to GA4, Meta Pixel, Google Ads, etc. Adding a new
 * destination later (e.g. TikTok) is a no-code change inside GTM.
 *
 * UTM/click-id capture: on first arrival from an ad, we persist all
 * attribution params to localStorage so we can attach them to form submissions
 * even after the user clicks around the site.
 */

const ATTRIB_KEY = 'shipmaster_attribution_v1';
const ATTRIB_TTL_DAYS = 90; // industry-standard lookback window

const ATTRIB_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',     // Google Ads click ID
  'fbclid',    // Meta Ads click ID
  'msclkid',   // Microsoft Ads click ID
  'ttclid',    // TikTok Ads click ID
];

/**
 * Pull attribution params from the current URL and persist them. Only writes
 * when at least one attribution param is present, so direct/organic visits
 * don't clobber a real ad-attributed session.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    for (const key of ATTRIB_PARAMS) {
      const value = params.get(key);
      if (value) found[key] = value;
    }
    if (Object.keys(found).length === 0) return;
    const record = {
      ...found,
      landing_page: window.location.pathname,
      referrer: document.referrer || null,
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(ATTRIB_KEY, JSON.stringify(record));
  } catch {
    // sessionStorage/localStorage may be blocked (private mode, cookie
    // restrictions). Attribution is non-essential — fail silently.
  }
}

/**
 * Read previously captured attribution. Returns {} if nothing stored or the
 * record is older than ATTRIB_TTL_DAYS.
 */
export function getAttribution() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(ATTRIB_KEY);
    if (!raw) return {};
    const record = JSON.parse(raw);
    const ageMs = Date.now() - new Date(record.captured_at).getTime();
    if (ageMs > ATTRIB_TTL_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(ATTRIB_KEY);
      return {};
    }
    return record;
  } catch {
    return {};
  }
}

/**
 * Push a typed event into dataLayer. Safe to call before GTM has loaded —
 * window.dataLayer is initialized by the GTM bootstrap snippet in index.html.
 */
export function track(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
    // Stamp every event with current path so cross-page funnels are easy to
    // build in GA4 explore reports.
    page_path: window.location.pathname,
  });
}

/**
 * Convenience: track a CTA click with consistent params. Call from any
 * onClick handler — pass a label that describes the button's intent and
 * the surrounding section it lives in.
 */
export function trackCtaClick({ label, location, destination, variant }) {
  track('cta_click', {
    cta_label: label,
    cta_location: location,
    cta_destination: destination || null,
    cta_variant: variant || null,
  });
}
